import React, { useState } from "react";
// TRANSLATION
import { useTranslation } from "react-i18next";
// ANT DESIGN COMPONENTS
import { Divider, InputNumber, Select, Switch } from "antd";
// COMPONENTS
import OpcUaTextField from "./OpcUaTextField";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import EnumHelpTable, { type EnumHelpRow } from "./EnumHelpTable";
// FLATBUFFER ENUMS
import { TimestampsToReturn } from "../../../../../flatbuffers/generated/opc-ua/timestamps-to-return";
// INTERFACES
import type { OpcUaSessionForm } from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Option } = Select;

export const BLANK_SESSION: OpcUaSessionForm = {
  sessionTimeoutMs: 60000,
  secureChannelLifetimeMs: 600000,
  requestTimeoutMs: 10000,
  keepAliveIntervalMs: 10000,
  sessionRetryLimit: 10,
  sessionRetryIntervalMs: 5000,
  preferredLocales: [],
  maxChunkCount: 0,
  maxMessageSize: 0,
  timestampsToReturn: TimestampsToReturn.Both,
};

const TIMESTAMPS_OPTIONS: { value: TimestampsToReturn; label: string }[] = [
  { value: TimestampsToReturn.Source, label: "Source" },
  { value: TimestampsToReturn.Server, label: "Server" },
  { value: TimestampsToReturn.Both, label: "Both" },
  { value: TimestampsToReturn.Neither, label: "Neither" },
];

/** The locales a comma-separated entry names: trimmed, blanks dropped. */
const parseLocalesCsv = (csv: string): string[] =>
  csv
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

/** Session and secure-channel tuning parameters. Optional — a switch toggles between server defaults (null) and an explicit config. */
export default function SessionEditor({
  session,
  onChangeSession,
}: {
  session: OpcUaSessionForm | null;
  onChangeSession: (session: OpcUaSessionForm | null) => void;
}) {
  const [t] = useTranslation("global");

  const onToggleEnabled = (enabled: boolean) =>
    onChangeSession(enabled ? { ...BLANK_SESSION } : null);
  const onChangeField = <K extends keyof OpcUaSessionForm>(
    field: K,
    value: OpcUaSessionForm[K],
  ) => {
    onChangeSession({ ...(session ?? BLANK_SESSION), [field]: value });
  };
  // The locales box shows what was typed, not the stored list re-joined. The
  // list has no room for a trailing comma or a blank entry, so rendering it
  // back ate each comma as it was typed, and typing "en-US,es-ES" saved the
  // single locale "en-USes-ES".
  //
  // The text follows the stored list only when the list changes to something
  // the text does not already say — switching tuning off and on, or an adapter
  // being loaded. Comparing against the last list seen, rather than re-parsing
  // on every render, is what stops a stored entry the parser would rewrite
  // (" en-US") from resetting the box forever.
  const storedLocalesCsv = (session?.preferredLocales ?? []).join(",");
  const [localesText, setLocalesText] = useState(storedLocalesCsv);
  const [seenLocalesCsv, setSeenLocalesCsv] = useState(storedLocalesCsv);
  if (storedLocalesCsv !== seenLocalesCsv) {
    setSeenLocalesCsv(storedLocalesCsv);
    if (parseLocalesCsv(localesText).join(",") !== storedLocalesCsv) {
      setLocalesText(storedLocalesCsv);
    }
  }
  const onChangeLocalesCsv = (csv: string) => {
    setLocalesText(csv);
    onChangeField("preferredLocales", parseLocalesCsv(csv));
  };

  const timestampsHelpRows: EnumHelpRow[] = [
    { value: "Source", meaning: t("general.opcUaTimestampsSourceDesc") },
    { value: "Server", meaning: t("general.opcUaTimestampsServerDesc") },
    { value: "Both", meaning: t("general.opcUaTimestampsBothDesc") },
    { value: "Neither", meaning: t("general.opcUaTimestampsNeitherDesc") },
  ];

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <div style={{ marginBottom: 16 }}>
        <OpcUaFieldLabel label="Configure session tuning (else server defaults)" />
        <div style={{ marginTop: 8 }}>
          <Switch checked={session !== null} onChange={onToggleEnabled} />
        </div>
      </div>
      {session && (
        <>
          {/* GROUP 1 — session lifecycle: how long things stay alive, how often
              they're renewed/retried. 6 fields -> 2 full rows of 3. */}
          <Divider orientation="left">{t("general.opcUaSessionGroupTimeouts")}</Divider>
          <div className="row">
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Session Timeout (ms)"
                tooltip={t("general.opcUaSessionTimeoutHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                value={session.sessionTimeoutMs}
                onChange={(v) => onChangeField("sessionTimeoutMs", v ?? 0)}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Secure Channel Lifetime (ms)"
                tooltip={t("general.opcUaSecureChannelLifetimeHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                value={session.secureChannelLifetimeMs}
                onChange={(v) => onChangeField("secureChannelLifetimeMs", v ?? 0)}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Request Timeout (ms)"
                tooltip={t("general.opcUaRequestTimeoutHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                value={session.requestTimeoutMs}
                onChange={(v) => onChangeField("requestTimeoutMs", v ?? 0)}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Keep-Alive Interval (ms)"
                tooltip={t("general.opcUaKeepAliveIntervalHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                value={session.keepAliveIntervalMs}
                onChange={(v) => onChangeField("keepAliveIntervalMs", v ?? 0)}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Session Retry Limit"
                tooltip={t("general.opcUaSessionRetryLimitHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={-1}
                value={session.sessionRetryLimit}
                onChange={(v) => onChangeField("sessionRetryLimit", v ?? 0)}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Session Retry Interval (ms)"
                tooltip={t("general.opcUaSessionRetryIntervalHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                value={session.sessionRetryIntervalMs}
                onChange={(v) => onChangeField("sessionRetryIntervalMs", v ?? 0)}
              />
            </div>
          </div>

          {/* GROUP 2 — wire/message framing + display language. 3 fields ->
              one full row of 3, then Preferred Locales as the leftover
              field, alone in its own 100%-width row. */}
          <Divider orientation="left">{t("general.opcUaSessionGroupMessageLocale")}</Divider>
          <div className="row">
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Max Chunk Count"
                tooltip={t("general.opcUaMaxChunkCountHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                value={session.maxChunkCount}
                onChange={(v) => onChangeField("maxChunkCount", v ?? 0)}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Max Message Size (bytes)"
                tooltip={t("general.opcUaMaxMessageSizeHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                value={session.maxMessageSize}
                onChange={(v) => onChangeField("maxMessageSize", v ?? 0)}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Timestamps To Return"
                tooltip={
                  <EnumHelpTable
                    valueHeader={t("general.opcUaEnumTableValue")}
                    meaningHeader={t("general.opcUaEnumTableMeaning")}
                    rows={timestampsHelpRows}
                  />
                }
              />
              <Select
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                value={session.timestampsToReturn}
                onChange={(v: TimestampsToReturn) => onChangeField("timestampsToReturn", v)}
              >
                {TIMESTAMPS_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="col-12 mt-2">
              <OpcUaTextField
                label="Preferred Locales (comma-separated, e.g. en-US,es-ES)"
                value={localesText}
                onChange={onChangeLocalesCsv}
                tooltip={t("general.opcUaPreferredLocalesHelp")}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
